import { z } from 'zod';
import { multiValueFilter } from '@/features/email-accounts/search-schema';
import { NOTIFICATION_CATEGORIES, NotificationReadState } from '@/lib/notifications';

/**
 * URL search-parameter schema for the `/notifications` route. The URL is the
 * single source of truth for the feed filters, so a refresh / deep link / back
 * button all reproduce the same view.
 *
 * - `category` → multi-select category filter, normalised exactly like the
 *   email-accounts `status`/`provider` filters (reusing the shared
 *   `multiValueFilter`): a lone value coerces to a one-element array, unknown/
 *   blank members are dropped + deduped, and an empty set collapses to
 *   `undefined` so an inactive filter leaves no `category` key in the URL.
 * - `readState` → single-select `all | unread | read`, defaulting to `all` on
 *   an absent/garbage value.
 *
 * `cursor`/`limit` are NOT URL params: cursor pagination is driven imperatively
 * by TanStack Query's `useInfiniteQuery` ("Load more"), not by a shareable URL
 * offset, so they'd only add churn to the history stack.
 */
export const notificationsSearchSchema = z.object({
  category: multiValueFilter(NOTIFICATION_CATEGORIES),
  readState: z.nativeEnum(NotificationReadState).catch(NotificationReadState.ALL),
});

export type NotificationsSearch = z.infer<typeof notificationsSearchSchema>;
